import { useCallback, useMemo } from 'react'
import { Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { DataTable } from '@/shared/ui/table/DataTable'
import type { PageResult } from '@/shared/ui/table/DataTable'
import type { TableState } from '@/shared/ui/table/tableState'
import { historyTimeColumns, networkColumns, terminateCauseColumn, trafficColumns } from './SessionColumns'
import { fetchSessionHistory, type SessionHistoryRow } from './sessionApi'

interface SessionHistoryTableProps {
  /**
   * Berilsa — faqat shu abonentniki va qidiruv maydoni chizilmaydi.
   * Berilmasa — umumiy tarix, qidiruv login bo'yicha ishlaydi.
   */
  username?: string
  /** Bitta sahifada ikkinchi jadval bo'lsa URL parametrlari to'qnashmasin */
  urlPrefix?: string
}

/**
 * Sessiyalar tarixi.
 *
 * ⚠️ JAMI SON KO'RSATILMAYDI. `SessionPage` da `total` YO'Q — faqat
 * `hasNext` bor (`SubscriberPage` dan farqli, u `total` beradi). Ya'ni
 * "1–25 / 1 240" deb yozish uchun ma'lumot yo'q va uni taxmin qilib
 * ko'rsatish panelning bilmagan narsasini bilgandek qilib ko'rsatishi
 * bo'lardi. Sahifalash tugmalari esa `hasNext` asosida ishlaydi
 * (`sessionApi.ts` dagi izoh).
 */
export function SessionHistoryTable({ username, urlPrefix }: SessionHistoryTableProps) {
  const { t } = useTranslation()

  const fetchPage = useCallback(
    (state: TableState, signal: AbortSignal): Promise<PageResult<SessionHistoryRow>> =>
      fetchSessionHistory(state, signal, username === undefined ? {} : { username }),
    [username],
  )

  const columns = useMemo<ColumnsType<SessionHistoryRow>>(() => {
    const base: ColumnsType<SessionHistoryRow> = []

    // Bitta abonent kartochkasida login ustuni ortiqcha — u sarlavhada bor
    if (username === undefined) {
      base.push({
        key: 'username',
        title: t('session.subscriber'),
        width: 160,
        fixed: 'left',
        render: (_, row) => <Typography.Text strong>{row.username}</Typography.Text>,
      })
    }

    return [
      ...base,
      ...networkColumns<SessionHistoryRow>(t),
      ...historyTimeColumns(t),
      ...trafficColumns<SessionHistoryRow>(t),
      terminateCauseColumn(t),
    ]
  }, [t, username])

  return (
    <DataTable<SessionHistoryRow>
      queryKey={[...queryKeys.sessions.history, username ?? 'all']}
      fetchPage={fetchPage}
      columns={columns}
      rowKey={(row) => String(row.radacctId)}
      searchable={username === undefined}
      searchPlaceholder={t('sessions.searchPlaceholder')}
      showTotal={false}
      scrollX={1200}
      {...(urlPrefix === undefined ? {} : { urlPrefix })}
    />
  )
}
